import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("wp_cart", []));
  const [recentlyViewed, setRecentlyViewed] = useState(() => load("wp_recent", []));
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => { localStorage.setItem("wp_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem("wp_recent", JSON.stringify(recentlyViewed)); }, [recentlyViewed]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === item.id && p.type === item.type);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + (item.qty || 1) };
        return next;
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((id, type) => {
    setItems((prev) => prev.filter((p) => !(p.id === id && p.type === type)));
  }, []);

  const updateQty = useCallback((id, type, qty) => {
    setItems((prev) => prev.map((p) => (p.id === id && p.type === type ? { ...p, qty: Math.max(1, qty) } : p)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const addRecentlyViewed = useCallback((p) => {
    if (!p) return;
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((x) => x.id !== p.id);
      return [{ id: p.id, name: p.name, image: p.image, price: p.price, type: p.type || "product", slug: p.slug }, ...filtered].slice(0, 8);
    });
  }, []);

  const subtotal = items.reduce((s, it) => s + it.price * it.qty, 0);
  const count = items.reduce((s, it) => s + it.qty, 0);

  return (
    <CartContext.Provider value={{
      items, isOpen, setIsOpen, addItem, removeItem, updateQty, clear, subtotal, count,
      recentlyViewed, addRecentlyViewed,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
